/**
 * custom-voice-search.js
 * Powers the voice search button rendered by snippets/custom-voice-search.liquid
 *
 * Features:
 *  - Uses Web Speech API (Chrome, Edge, Safari 14.5+)
 *  - Reveals the mic button only when SpeechRecognition is supported
 *  - Fills the search input with interim + final results
 *  - Shows animated wave bars while listening
 *  - 2-second silence timeout auto-stops listening
 *  - Stops cleanly when the search panel closes (custom event "cvs:stop")
 *  - Multiple buttons on the same page are each initialised independently
 */

(function () {
  'use strict';

  var SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

  // Bail out silently if browser doesn't support the API
  if (!SpeechRecognition) return;

  var SILENCE_MS = 2000;

  function initVoiceBtn(btn) {
    if (!btn || btn.dataset.cvsInit) return;
    btn.dataset.cvsInit = '1';

    var inputId  = btn.dataset.cvsInputId;
    var input    = inputId ? document.getElementById(inputId) : null;
    var iconEl   = btn.querySelector('[data-cvs-icon]');
    var wavesEl  = btn.querySelector('[data-cvs-waves]');

    // Ensure waves are hidden before we reveal the button
    if (wavesEl) wavesEl.hidden = true;
    if (iconEl)  iconEl.hidden  = false;

    // Reveal the button now we know the browser supports speech AND
    // the click listener is about to be attached below
    btn.hidden = false;

    var recognition    = null;
    var isListening    = false;
    var silenceTimeout = null;

    /* ── STATE ─────────────────────────────────────────────── */
    function setListening(listening) {
      isListening = listening;
      btn.setAttribute('aria-pressed', listening ? 'true' : 'false');
      if (iconEl)  iconEl.hidden  = listening;
      if (wavesEl) wavesEl.hidden = !listening;
    }

    function stopListening() {
      clearSilence();
      if (recognition) { try { recognition.stop(); } catch (e) {} }
      setListening(false);
    }

    function clearSilence() {
      if (silenceTimeout) { clearTimeout(silenceTimeout); silenceTimeout = null; }
    }

    function resetSilenceTimer() {
      clearSilence();
      silenceTimeout = setTimeout(stopListening, SILENCE_MS);
    }

    /* ── RECOGNITION ───────────────────────────────────────── */
    function startListening() {
      recognition = new SpeechRecognition();
      recognition.continuous     = true;
      recognition.interimResults = true;
      recognition.lang           = document.documentElement.lang || 'en-US';

      recognition.onstart = function () {
        setListening(true);
        resetSilenceTimer();
      };

      recognition.onresult = function (event) {
        resetSilenceTimer();
        var newFinals = '';
        for (var i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            newFinals += (newFinals ? ' ' : '') + event.results[i][0].transcript.trim();
          }
        }
        if (newFinals && input) {
          input.value = (input.value ? input.value + ' ' : '') + newFinals;
          // Trigger the search debounce in custom-search.js
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
      };

      recognition.onend  = function () { setListening(false); };
      recognition.onerror = function (e) {
        if (e.error !== 'aborted') setListening(false);
      };

      try {
        recognition.start();
      } catch (e) {
        setListening(false);
      }
    }

    /* ── BUTTON CLICK ──────────────────────────────────────── */
    btn.addEventListener('click', function () {
      if (isListening) { stopListening(); } else { startListening(); }
    });

    /* ── GLOBAL STOP EVENT (fired by custom-search.js on panel close) ── */
    document.addEventListener('cvs:stop', stopListening);

    /* ── STOP ON FORM SUBMIT ───────────────────────────────── */
    if (input) {
      var form = input.closest('form');
      if (form) form.addEventListener('submit', stopListening);
    }
  }

  /* ──────────────────────────────────────────────────────────────
     BOOT — initialise all voice buttons present now and in future
  ────────────────────────────────────────────────────────────── */
  function initAll() {
    Array.from(document.querySelectorAll('[data-cvs-btn]')).forEach(initVoiceBtn);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Handle Shopify theme editor section loads
  document.addEventListener('shopify:section:load', initAll);

  // Also observe dynamically added buttons (e.g. if search panel is injected)
  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('[data-cvs-btn]')) { initVoiceBtn(node); }
          Array.from(node.querySelectorAll('[data-cvs-btn]')).forEach(initVoiceBtn);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

})();
